import { useState } from "react";

// Term (season) manager. The live schedule is always the "active term"; saved
// terms are independent snapshots you can switch to, duplicate, export, or
// delete. Switching auto-snapshots the current term first, so nothing is lost.

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function downloadTerm(term) {
  const payload = {
    name: term.name,
    savedAt: term.savedAt,
    players: term.players,
    groups: term.groups,
    coaches: term.coaches,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `kac_term_${term.name.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function TermsModal({
  activeTermName, terms, players, groups, coaches,
  onSaveTerm, onStartNewTerm, onSwitchTerm, onDeleteTerm, onRenameTerm, onDuplicateTerm,
  onClose, onNotify,
}) {
  const [saveName, setSaveName] = useState(activeTermName || "");
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const sortedTerms = [...terms].sort((a, b) => (b.savedAt || "").localeCompare(a.savedAt || ""));

  function handleSave() {
    const name = saveName.trim();
    if (!name) return;
    onSaveTerm(name);
    onNotify?.({ message: `Saved “${name}”` });
  }

  function handleStartNew() {
    const name = newName.trim();
    if (!name) return;
    if (terms.some(t => t.name === name)) {
      onNotify?.({ message: `A term named “${name}” already exists — pick another name.` });
      return;
    }
    onStartNewTerm(name);
    setNewName("");
    onNotify?.({ message: `Started new term “${name}” (clone of ${activeTermName || "current"})` });
  }

  function handleSwitch(term) {
    if (term.name === activeTermName) return;
    if (!window.confirm(`Switch the live schedule to “${term.name}”?\n\nYour current term${activeTermName ? ` (“${activeTermName}”)` : ""} is saved automatically first.`)) return;
    onSwitchTerm(term.id);
    onNotify?.({ message: `Switched to “${term.name}”` });
  }

  function handleDelete(term) {
    if (!window.confirm(`Delete the saved term “${term.name}”? This can't be undone.`)) return;
    onDeleteTerm(term.id);
    onNotify?.({ message: `Deleted “${term.name}”` });
  }

  function handleDuplicate(term) {
    const name = window.prompt(`Duplicate “${term.name}” as:`, `${term.name} (copy)`);
    if (!name || !name.trim()) return;
    if (terms.some(t => t.name === name.trim())) {
      onNotify?.({ message: `A term named “${name.trim()}” already exists.` });
      return;
    }
    onDuplicateTerm(term.id, name.trim());
    onNotify?.({ message: `Duplicated to “${name.trim()}”` });
  }

  function commitRename(term) {
    const name = renameValue.trim();
    if (name && name !== term.name) onRenameTerm(term.id, name);
    setRenamingId(null);
  }

  const inputCls = "border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Terms</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Active: <span className="font-semibold text-gray-700">{activeTermName || "Untitled term"}</span>
              {" · "}{players?.length ?? 0} players · {groups?.length ?? 0} groups
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-6">
          {/* Save current */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Save current schedule</h3>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="e.g. Fall 2026"
                className={`${inputCls} flex-1 min-w-[12rem]`}
              />
              <button
                onClick={handleSave}
                disabled={!saveName.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
              >
                {terms.some(t => t.name === saveName.trim()) ? "Update snapshot" : "Save as term"}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Saves a snapshot of the current players, groups, and coaches.</p>
          </section>

          {/* Start new term */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Start a new term</h3>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Winter 2027"
                className={`${inputCls} flex-1 min-w-[12rem]`}
              />
              <button
                onClick={handleStartNew}
                disabled={!newName.trim()}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
              >
                Start new term →
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Clones the current term as the starting point (your current term is saved first), then you edit the new one.
            </p>
          </section>

          {/* Saved terms */}
          <section>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Saved terms ({terms.length})
            </h3>
            {sortedTerms.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-lg">
                No saved terms yet. Save the current schedule above to start.
              </p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {sortedTerms.map(term => {
                  const isActive = term.name === activeTermName;
                  return (
                    <div key={term.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 border-b border-gray-100 last:border-b-0">
                      <div className="min-w-0 flex-1 basis-48">
                        {renamingId === term.id ? (
                          <input
                            type="text"
                            value={renameValue}
                            autoFocus
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(term)}
                            onKeyDown={e => { if (e.key === "Enter") commitRename(term); if (e.key === "Escape") setRenamingId(null); }}
                            className={`${inputCls} py-1 text-sm w-full`}
                          />
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm truncate">{term.name}</span>
                            {isActive && (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">Active</span>
                            )}
                            <button
                              onClick={() => { setRenamingId(term.id); setRenameValue(term.name); }}
                              className="text-xs text-gray-400 hover:text-gray-700"
                              title="Rename"
                            >
                              ✎
                            </button>
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {(term.players?.length ?? 0)} players · {(term.groups?.length ?? 0)} groups · saved {fmtDate(term.savedAt)}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1 flex-shrink-0 ml-auto">
                        <button
                          onClick={() => handleSwitch(term)}
                          disabled={isActive}
                          className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default"
                          title={isActive ? "This term is already live" : "Make this the live schedule"}
                        >
                          {isActive ? "Live" : "Switch"}
                        </button>
                        <button onClick={() => handleDuplicate(term)} className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-600 hover:bg-gray-50" title="Duplicate">Duplicate</button>
                        <button onClick={() => downloadTerm(term)} className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-600 hover:bg-gray-50" title="Export as JSON">Export</button>
                        <button onClick={() => handleDelete(term)} className="text-xs border border-red-200 text-red-600 rounded px-2 py-1 hover:bg-red-50" title="Delete">Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
